"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { toast } from 'sonner'
import { Wand2, Settings, FileText } from "lucide-react"

interface ContentGeneratorModalProps {
  isOpen: boolean
  onClose: () => void
  onGenerate: (content: string, title?: string) => void
  initialContext?: string
}

interface GenerationConfig {
  context: string
  characterLimit: number
  paragraphCount: number
  tone: string
  generateTitle: boolean
}

const toneOptions = [
  { value: 'professional', label: 'Profissional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'journalistic', label: 'Jornalístico' },
  { value: 'technical', label: 'Técnico' }
]

export function ContentGeneratorModal({ isOpen, onClose, onGenerate, initialContext = '' }: ContentGeneratorModalProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [config, setConfig] = useState<GenerationConfig>({
    context: initialContext,
    characterLimit: 1500,
    paragraphCount: 4,
    tone: 'journalistic',
    generateTitle: false
  })

  const handleGenerate = async () => {
    if (!config.context.trim()) {
      toast.warning('Contexto necessário', {
        description: 'Digite um contexto ou assunto para gerar o conteúdo.',
        duration: 3000,
      })
      return
    }

    setIsGenerating(true)
    
    try {
      const response = await fetch('/dashboard/posts/generate_content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          context: config.context,
          character_limit: config.characterLimit,
          paragraph_count: config.paragraphCount,
          tone: config.tone,
          generate_title: config.generateTitle
        })
      })

      const result = await response.json()

      if (result.success) {
        onGenerate(result.content, result.title)
        onClose()
        toast.success('Conteúdo gerado com sucesso!', {
          description: `Gerado ${result.character_count} caracteres de conteúdo ${toneOptions.find(t => t.value === config.tone)?.label.toLowerCase()}.`,
          duration: 3000,
        })
      } else {
        throw new Error(result.errors?.join(', ') || 'Erro desconhecido')
      }
    } catch (error) {
      console.error('Erro ao gerar conteúdo:', error)
      toast.error('Erro ao gerar conteúdo', {
        description: error instanceof Error ? error.message : 'Tente novamente em alguns instantes.',
        duration: 5000,
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const updateConfig = (key: keyof GenerationConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Gerador de Conteúdo IA
          </DialogTitle>
          <DialogDescription>
            Configure os parâmetros para gerar conteúdo profissional com inteligência artificial
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Contexto/Assunto */}
          <div className="space-y-2">
            <Label htmlFor="context">Contexto ou Assunto *</Label>
            <Textarea
              id="context"
              placeholder="Ex: Como otimizar SEO para blogs, Tendências de marketing digital 2024, etc."
              value={config.context}
              onChange={(e) => updateConfig('context', e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Descreva o tema ou assunto sobre o qual você quer gerar conteúdo
            </p>
          </div>

          {/* Tom do conteúdo */}
          <div className="space-y-2">
            <Label htmlFor="tone">Tom do Conteúdo</Label>
            <Select value={config.tone} onValueChange={(value) => updateConfig('tone', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {toneOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantidade de parágrafos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="paragraphCount">Quantidade de Parágrafos</Label>
              <span className="text-sm text-muted-foreground">{config.paragraphCount} parágrafos</span>
            </div>
            <Slider
              id="paragraphCount"
              min={2}
              max={10}
              step={1}
              value={[config.paragraphCount]}
              onValueChange={(value) => updateConfig('paragraphCount', value[0])}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Mínimo (2)</span>
              <span>Médio (6)</span>
              <span>Máximo (10)</span>
            </div>
          </div>

          {/* Limite de caracteres */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="characterLimit">Tamanho do Conteúdo</Label>
              <span className="text-sm text-muted-foreground">{config.characterLimit} caracteres</span>
            </div>
            <Slider
              id="characterLimit"
              min={500}
              max={5000}
              step={100}
              value={[config.characterLimit]}
              onValueChange={(value) => updateConfig('characterLimit', value[0])}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Curto (500)</span>
              <span>Médio (2500)</span>
              <span>Longo (5000)</span>
            </div>
          </div>

          {/* Gerar título */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="generateTitle">Gerar Título</Label>
              <p className="text-xs text-muted-foreground">
                Gerar um título atrativo junto com o conteúdo
              </p>
            </div>
            <Switch
              id="generateTitle"
              checked={config.generateTitle}
              onCheckedChange={(checked) => updateConfig('generateTitle', checked)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            Cancelar
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating || !config.context.trim()}>
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Gerando...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Gerar Conteúdo
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}